import {
  useCallback,
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { NotificationChange } from '@/types/doc-changes'

export type FieldIdentifier = `${string}_${string}`
export type FieldUpdateState = {
  forIndicator: boolean
}

// How long after the system changes a field we still warn the user when they
// edit that same field.
export const SYSTEM_CHANGE_WINDOW_MS = 6000

// How long a change set id waits for the slower of its two copies —
// websocket or sync response — before being dropped.
const APPLIED_CHANGE_SET_IDS_TTL_MS = 60000

export type NotificationChangeApplier = (change: NotificationChange) => void
export function buildFieldIdentifier(
  fieldId?: string | null,
  rowId?: string | null,
): FieldIdentifier {
  return `${fieldId || ''}_${rowId || ''}`
}

export type DocumentsChangeSetsContextValue = {
  documentsChangeSetIdsRef: React.RefObject<Record<string, Set<string>>>
  documentLastChangeSetSentAt: Record<string, Date>
  setDocumentLastChangeSetSentAt: React.Dispatch<
    React.SetStateAction<Record<string, Date>>
  >
  fieldsBeingUpdated: Record<FieldIdentifier, FieldUpdateState>
  markFieldsUpdating: (
    fieldIdentifiers: FieldIdentifier[],
    forIndicator: boolean,
  ) => void
  clearAllFieldsBeingUpdated: () => void
  clearFieldsBeingUpdated: (fieldIdentifiers: FieldIdentifier[]) => void
  isFieldUpdating: (fieldId?: string | null, rowId?: string | null) => boolean
  isFieldUpdatingForIndicator: (
    fieldId?: string | null,
    rowId?: string | null,
  ) => boolean
  markFieldsChangedBySystem: (fieldIdentifiers: FieldIdentifier[]) => void
  consumeRecentSystemChange: (
    fieldId?: string | null,
    rowId?: string | null,
  ) => boolean
  clearSystemChanges: () => void
  changeAppliersRef: React.RefObject<Record<string, NotificationChangeApplier>>
  registerChangeApplier: (
    docId: string,
    applier: NotificationChangeApplier,
  ) => () => void
  appliedChangeSetIdsRef: React.RefObject<Set<string>>
  markChangeSetIdsApplied: (changeSetIds: string[]) => void
  tryConsumeAppliedChangeSetIds: (changeSetIds: string[]) => boolean
  clearPendingChangeSetIds: (docId: string, changeSetIds: string[]) => void
}

const DocumentsChangeSetsContext =
  createContext<DocumentsChangeSetsContextValue>(
    {} as DocumentsChangeSetsContextValue,
  )

export const useDocumentsChangeSetsContext = () =>
  useContext(DocumentsChangeSetsContext)

type DocumentsChangeSetsProviderProps = {
  children: ReactNode
}

export default function DocumentsChangeSetsProvider({
  children,
}: DocumentsChangeSetsProviderProps) {
  const documentsChangeSetIdsRef = useRef<Record<string, Set<string>>>({})

  const [documentLastChangeSetSentAt, setDocumentLastChangeSetSentAt] =
    useState<Record<string, Date>>({})

  const [fieldsBeingUpdated, setFieldsBeingUpdated] = useState<
    Record<FieldIdentifier, FieldUpdateState>
  >({})

  const markFieldsUpdating = useCallback(
    (fieldIdentifiers: FieldIdentifier[], forIndicator: boolean) => {
      if (!fieldIdentifiers.length) return

      setFieldsBeingUpdated((prev) => {
        const next = { ...prev }
        fieldIdentifiers.forEach((fieldIdentifier) => {
          next[fieldIdentifier] = {
            forIndicator:
              Boolean(next[fieldIdentifier]?.forIndicator) || forIndicator,
          }
        })
        return next
      })
    },
    [],
  )

  const clearAllFieldsBeingUpdated = useCallback(() => {
    setFieldsBeingUpdated((prev) => (Object.keys(prev).length ? {} : prev))
  }, [])

  const clearFieldsBeingUpdated = useCallback(
    (fieldIdentifiers: FieldIdentifier[]) => {
      if (!fieldIdentifiers.length) return
      setFieldsBeingUpdated((prev) => {
        if (!Object.keys(prev).length) return prev
        const next = { ...prev }
        fieldIdentifiers.forEach((id) => {
          delete next[id]
        })
        return next
      })
    },
    [],
  )

  // A ref rather than state: it has to survive clearAllFieldsBeingUpdated,
  // which runs on every notification, and stamping must not re-render.
  const lastSystemChangeAtRef = useRef<Record<FieldIdentifier, number>>({})

  const markFieldsChangedBySystem = useCallback(
    (fieldIdentifiers: FieldIdentifier[]) => {
      if (!fieldIdentifiers.length) return
      const now = Date.now()
      fieldIdentifiers.forEach((fieldIdentifier) => {
        lastSystemChangeAtRef.current[fieldIdentifier] = now
      })
    },
    [],
  )

  // Returns whether the system changed this field within the window, and drops
  // the stamp either way — so a warning fires once per system change rather
  // than on every keystroke, and stale stamps don't pile up.
  const consumeRecentSystemChange = useCallback(
    (fieldId?: string | null, rowId?: string | null) => {
      if (!fieldId || !rowId) return false
      const fieldIdentifier = buildFieldIdentifier(fieldId, rowId)
      const changedAt = lastSystemChangeAtRef.current[fieldIdentifier]
      if (changedAt === undefined) return false
      delete lastSystemChangeAtRef.current[fieldIdentifier]
      return Date.now() - changedAt <= SYSTEM_CHANGE_WINDOW_MS
    },
    [],
  )

  // Fields the user never edits keep their stamp, so drop the whole map when
  // the document changes rather than letting it grow all session.
  const clearSystemChanges = useCallback(() => {
    lastSystemChangeAtRef.current = {}
  }, [])

  const isFieldUpdatingForIndicator = useCallback(
    (fieldId?: string | null, rowId?: string | null) => {
      if (!fieldId || !rowId) return false
      const fieldIdentifier = buildFieldIdentifier(fieldId, rowId)
      return Boolean(fieldsBeingUpdated[fieldIdentifier]?.forIndicator)
    },
    [fieldsBeingUpdated],
  )

  const isFieldUpdating = useCallback(
    (fieldId?: string | null, rowId?: string | null) => {
      if (!fieldId || !rowId) return false
      const fieldIdentifier = buildFieldIdentifier(fieldId, rowId)
      return Object.hasOwn(fieldsBeingUpdated, fieldIdentifier)
    },
    [fieldsBeingUpdated],
  )

  const changeAppliersRef = useRef<Record<string, NotificationChangeApplier>>(
    {},
  )

  const registerChangeApplier = useCallback(
    (docId: string, applier: NotificationChangeApplier) => {
      changeAppliersRef.current[docId] = applier
      return () => {
        if (changeAppliersRef.current[docId] === applier) {
          delete changeAppliersRef.current[docId]
        }
      }
    },
    [],
  )

  const appliedChangeSetIdsRef = useRef<Set<string>>(new Set())
  const appliedChangeSetIdsAddedAtRef = useRef<Map<string, number>>(new Map())

  const markChangeSetIdsApplied = useCallback((changeSetIds: string[]) => {
    if (!changeSetIds.length) return
    const now = Date.now()
    changeSetIds.forEach((id) => {
      appliedChangeSetIdsRef.current.add(id)
      appliedChangeSetIdsAddedAtRef.current.set(id, now)
    })
  }, [])

  // True, and clears the ids, only when every id was already applied by the
  // other path. A partial match changes nothing and returns false, so the
  // caller still applies the notification itself.
  const tryConsumeAppliedChangeSetIds = useCallback(
    (changeSetIds: string[]) => {
      if (
        !changeSetIds.length ||
        !changeSetIds.every((id) => appliedChangeSetIdsRef.current.has(id))
      )
        return false

      changeSetIds.forEach((id) => {
        appliedChangeSetIdsRef.current.delete(id)
        appliedChangeSetIdsAddedAtRef.current.delete(id)
      })
      return true
    },
    [],
  )

  // Ids normally leave as soon as the second copy arrives. This sweep only
  // catches the ones that never get matched, so the set can't grow forever.
  useEffect(() => {
    const interval = setInterval(() => {
      const cutoff = Date.now() - APPLIED_CHANGE_SET_IDS_TTL_MS
      appliedChangeSetIdsAddedAtRef.current.forEach((addedAt, id) => {
        if (addedAt > cutoff) return
        appliedChangeSetIdsRef.current.delete(id)
        appliedChangeSetIdsAddedAtRef.current.delete(id)
      })
    }, APPLIED_CHANGE_SET_IDS_TTL_MS)
    return () => clearInterval(interval)
  }, [])

  const clearPendingChangeSetIds = useCallback(
    (docId: string, changeSetIds: string[]) => {
      const pending = documentsChangeSetIdsRef.current[docId]
      if (!pending) return
      changeSetIds.forEach((id) => pending.delete(id))
      if (pending.size) return

      delete documentsChangeSetIdsRef.current[docId]
      setDocumentLastChangeSetSentAt((prev) => {
        if (!prev[docId]) return prev
        const updated = { ...prev }
        delete updated[docId]
        return updated
      })
    },
    [],
  )

  return (
    <DocumentsChangeSetsContext.Provider
      value={{
        documentsChangeSetIdsRef,
        documentLastChangeSetSentAt,
        setDocumentLastChangeSetSentAt,
        fieldsBeingUpdated,
        markFieldsUpdating,
        clearAllFieldsBeingUpdated,
        clearFieldsBeingUpdated,
        isFieldUpdating,
        isFieldUpdatingForIndicator,
        markFieldsChangedBySystem,
        consumeRecentSystemChange,
        clearSystemChanges,
        changeAppliersRef,
        registerChangeApplier,
        appliedChangeSetIdsRef,
        markChangeSetIdsApplied,
        tryConsumeAppliedChangeSetIds,
        clearPendingChangeSetIds,
      }}
    >
      {children}
    </DocumentsChangeSetsContext.Provider>
  )
}
